// supabase/functions/_shared/booking/settings.ts
// Rep booking settings (spec booking §1): shapes, defaults and validation shared by the edge functions, the settings
// page and the demo. SQL twins in 53_gavion_booking.sql: private.booking_default_hours / booking_default_reminders
// (scripts/gavion/opsParity.test.ts pins them), booking_valid_hours, booking_valid_reminders, booking_slugify.
export type IsoDay = "1" | "2" | "3" | "4" | "5" | "6" | "7";
export const ISO_DAYS: readonly IsoDay[] = ["1", "2", "3", "4", "5", "6", "7"];
export type TimeRange = [string, string];
export type WeeklyHours = Partial<Record<IsoDay, TimeRange[]>>;
export type ReminderPrefs = {
  h24: boolean;
  morning: boolean;
  h1: boolean;
  custom_minutes: number | null;
  custom_line: string | null;
};

export const DEFAULT_TZ = "America/Toronto";
export const DEFAULT_NOTICE_HOURS = 4;
export const DEFAULT_HORIZON_DAYS = 21;
export const DEFAULT_WEEKLY_HOURS: WeeklyHours = {
  "1": [["09:00", "16:30"]],
  "2": [["09:00", "16:30"]],
  "3": [["09:00", "16:30"]],
  "4": [["09:00", "16:30"]],
  "5": [["09:00", "16:30"]],
  "6": [],
  "7": [],
};
export const DEFAULT_REMINDERS: ReminderPrefs = {
  h24: true,
  morning: false,
  h1: true,
  custom_minutes: null,
  custom_line: null,
};
// Slugs a rep cannot take (preflight L13). SQL twin: the array in save_booking_settings (53_gavion_booking.sql);
// scripts/gavion/opsParity.test.ts pins it.
export const RESERVED_SLUGS: readonly string[] = [
  "demo",
  "gerer",
  "admin",
  "api",
  "app",
  "www",
  "static",
  "assets",
  "login",
  "auth",
  "support",
  "gavion",
];

// Final review I2: the zones a rep may pick. America/Vancouver and America/Edmonton are left out until ICU (functions,
// browser) and PostgreSQL's tzdata agree on their rules from 2026-11-01 (runbook docs/runbooks/booking.md §3); a zone
// already saved stays savable. SQL twin: the array in save_booking_settings (53_gavion_booking.sql); opsParity pins it.
export const BOOKING_TIME_ZONES: readonly string[] = [
  "America/Toronto",
  "America/Halifax",
  "America/St_Johns",
  "America/Winnipeg",
  "America/Regina",
];
export const bookingTzAllowed = (tz: string, savedTz: string | null | undefined): boolean =>
  BOOKING_TIME_ZONES.includes(tz) || (!!savedTz && tz === savedTz);

export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
const START_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const END_RE = /^(([01]\d|2[0-3]):[0-5]\d|24:00)$/;
const REMINDER_KEYS = new Set(["h24", "morning", "h1", "custom_minutes", "custom_line"]);

/** Lower-case slug: accents dropped, other runs become one dash, 40 characters at most ("" when nothing is left). */
export function slugify(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/, "");
}

/**
 * {"1".."7": [["HH:MM","HH:MM"], …]}: at most 4 ranges a day, start < end, end may be 24:00; minutes on the 15-minute grid;
 * a day's ranges sorted and not overlapping (a range may start where the previous one ends). SQL twin: booking_valid_hours.
 */
export function validWeeklyHours(v: unknown): v is WeeklyHours {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  for (const [day, ranges] of Object.entries(v)) {
    if (!/^[1-7]$/.test(day) || !Array.isArray(ranges) || ranges.length > 4)
      return false;
    let prevEnd = "00:00";
    for (const r of ranges) {
      if (!Array.isArray(r) || r.length !== 2) return false;
      const [s, e] = r as unknown[];
      if (typeof s !== "string" || typeof e !== "string") return false;
      if (!START_RE.test(s) || !END_RE.test(e) || s >= e) return false;
      if (Number(s.slice(3)) % 15 !== 0 || Number(e.slice(3)) % 15 !== 0 || s < prevEnd) return false;
      prevEnd = e;
    }
  }
  return true;
}

export function validReminders(v: unknown): v is ReminderPrefs {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  if (Object.keys(o).some((k) => !REMINDER_KEYS.has(k))) return false;
  if (typeof o.h24 !== "boolean" || typeof o.morning !== "boolean" || typeof o.h1 !== "boolean")
    return false;
  const m = o.custom_minutes ?? null;
  if (m !== null && !(typeof m === "number" && Number.isInteger(m) && m >= 15 && m <= 10080))
    return false;
  const line = o.custom_line ?? null;
  return line === null || (typeof line === "string" && line.length <= 300);
}
